if (!customElements.get('bp-feel')) {
    customElements.define('bp-feel', class extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.bp-feel__stage');
        this.scenes = [...this.querySelectorAll('.bp-feel__scene')];
        this.dots = [...this.querySelectorAll('.bp-feel__dots li')];
        this.bars = [...this.querySelectorAll('.bp-feel__bars button')];
        this.counter = this.querySelector('[data-count]');
        this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mq = window.matchMedia('(max-width: 749px)');
        this.buildArch();
        this.tick = this.tick.bind(this);
        this.onScroll = () => { if (!this.swipe && !this.raf) this.raf = requestAnimationFrame(this.tick); };
        this.onSwipe = () => {
          if (!this.swipe) return;
          const i = Math.round(this.stage.scrollLeft / Math.max(1, this.stage.clientWidth));
          if (i > 0) this.classList.add('is-swiped');
          if (this.seen && i !== this.current) this.setCurrent(i, true);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.stage.addEventListener('scroll', this.onSwipe, { passive: true });
        this.bars.forEach((b) => b.addEventListener('click', () => {
          this.stage.scrollTo({ left: this.stage.clientWidth * Number(b.dataset.go), behavior: this.reduce ? 'auto' : 'smooth' });
        }));
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting && this.swipe && !this.seen) { this.seen = true; this.setCurrent(this.current < 0 ? 0 : this.current, true); }
          });
        }, { threshold: 0.4 });
        this.io.observe(this);
        this.setMode = this.setMode.bind(this);
        this.mq.addEventListener ? this.mq.addEventListener('change', this.setMode) : this.mq.addListener(this.setMode);
        this.setMode();
      }
      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.io) this.io.disconnect();
      }
      setMode() {
        this.swipe = this.mq.matches;
        this.classList.toggle('is-swipe', this.swipe);
        this.classList.toggle('is-enhanced', !this.swipe);
        this.current = -1;
        if (this.swipe) {
          this.stage.scrollLeft = 0;
          if (this.seen) this.setCurrent(0, true);
        } else {
          this.tick();
        }
      }
      buildArch() {
        // Scene 3: a baby's lower arch seen from above. It starts narrow, with crowded, twisted teeth.
        // With each "chew" it widens until every tooth sits neatly with room to spare.
        const svg = this.querySelector('.bp-feel__arch2');
        if (!svg) return;
        const ns = 'http://www.w3.org/2000/svg';
        const tg = svg.querySelector('.bp-feel__teeth');
        // Front to back: central incisor, lateral incisor, canine, first molar, second molar (width along arch, depth)
        const sizes = [[25, 17], [21, 16], [24, 20], [34, 29], [40, 32]];
        const gap = 6, overlap = 0.72;
        const even = [], tight = [];
        let a = gap / 2, b = 0;
        sizes.forEach(([w]) => {
          even.push(a + w / 2); a += w + gap;
          tight.push(b + (w * overlap) / 2); b += w * overlap;
        });
        const teeth = [];
        for (let k = sizes.length - 1; k >= 0; k--) {
          [-1, 1].forEach((side) => {
            const [w, h] = sizes[k];
            const r = document.createElementNS(ns, 'rect');
            r.setAttribute('width', w); r.setAttribute('height', h);
            r.setAttribute('x', -w / 2); r.setAttribute('y', -h / 2);
            r.setAttribute('rx', Math.min(8, h / 2.4));
            tg.appendChild(r);
            teeth.push({ el: r, side, k });
          });
        }
        this.arch = {
          svg, teeth, even, tight, t: -1,
          g: svg.querySelector('.bp-feel__archg2'),
          gum: svg.querySelector('.bp-feel__arch-gum'),
          twist: { '-1': [10, -24, 18, -6, 0], '1': [-14, 20, -12, 5, 0] },
          shift: { '-1': [2, 7, -6, 3, 0], '1': [-3, -6, 8, -2, 0] }
        };
        this.drawArch(0, 0);
      }
      chew(t) {
        // Progress moves in six "chews": each one widens the arch a step, with a small squeeze as it bites.
        if (t >= 1) return [1, 0];
        const n = 6, seg = t * n, k = Math.floor(seg), f = seg - k;
        const e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
        return [(k + e) / n, Math.sin(Math.PI * f) * 0.06];
      }
      drawArch(p, squeeze) {
        const A = this.arch;
        if (!A) return;
        p = Math.min(Math.max(p, 0), 1);
        const lerp = (x, y, t) => x + (y - x) * t;
        const cx = 160, top = 40;
        const W = lerp(58, 112, p), D = lerp(96, 120, p), base = top + D;
        A.gum.setAttribute('d', `M${(cx - W).toFixed(2)} ${(base + 6).toFixed(2)} L${(cx - W).toFixed(2)} ${base.toFixed(2)} A${W.toFixed(2)} ${D.toFixed(2)} 0 0 1 ${(cx + W).toFixed(2)} ${base.toFixed(2)} L${(cx + W).toFixed(2)} ${(base + 6).toFixed(2)}`);
        // Sample one quarter of the arch, from the midline at the front to the back
        const N = 48, pts = [];
        let len = 0, px = 0, py = top;
        for (let j = 0; j <= N; j++) {
          const u = (Math.PI / 2) * (j / N);
          const x = W * Math.sin(u), y = top + D * (1 - Math.cos(u));
          if (j) len += Math.hypot(x - px, y - py);
          pts.push([x, y, len]); px = x; py = y;
        }
        const at = (s) => {
          s = Math.min(Math.max(s, 0), len);
          let j = 1;
          while (j < N && pts[j][2] < s) j++;
          const [x0, y0, s0] = pts[j - 1], [x1, y1, s1] = pts[j];
          const f = (s - s0) / Math.max(1e-6, s1 - s0);
          const dx = x1 - x0, dy = y1 - y0, d = Math.hypot(dx, dy) || 1;
          return [x0 + dx * f, y0 + dy * f, dx / d, dy / d];
        };
        const q = 1 - p;
        A.teeth.forEach((t) => {
          let [x, y, tx, ty] = at(lerp(A.tight[t.k], A.even[t.k], p));
          const off = A.shift[t.side][t.k] * q;
          x += ty * off; y -= tx * off; // nudge in or out of line while crowded
          const ang = (Math.atan2(ty, tx * t.side) * 180) / Math.PI + A.twist[t.side][t.k] * q;
          t.el.setAttribute('transform', `translate(${(cx + t.side * x).toFixed(2)} ${y.toFixed(2)}) rotate(${ang.toFixed(2)})`);
        });
        A.g.setAttribute('transform', `translate(0 ${top}) scale(1 ${(1 - (squeeze || 0)).toFixed(3)}) translate(0 ${-top})`);
        A.svg.style.setProperty('--arch', p.toFixed(3));
      }
      setCurrent(i, animate) {
        this.current = i;
        this.dataset.scene = i;
        this.scenes.forEach((s, k) => s.classList.toggle('is-current', k === i));
        this.dots.forEach((d, k) => d.classList.toggle('is-current', k === i));
        this.bars.forEach((b, k) => { b.classList.toggle('is-done', k <= i); b.setAttribute('aria-current', k === i ? 'step' : 'false'); });
        this.scenes.forEach((s, k) => s.querySelectorAll('video').forEach((v) => {
          if (k === i && v.offsetParent !== null) { v.play && v.play().catch(() => {}); }
          else if (v.pause) v.pause();
        }));
        if (!animate) return;
        if (this.anim) cancelAnimationFrame(this.anim);
        for (let k = 0; k < this.scenes.length; k++) this.style.setProperty('--p' + (k + 1), k < i ? 1 : 0);
        if (this.reduce) { this.render(i, 1); return; }
        const start = performance.now(), dur = i === 3 ? 2800 : i === 2 ? 3400 : 1500;
        const step = (t) => {
          const local = Math.min(1, (t - start) / dur);
          this.render(i, local);
          if (local < 1 && this.current === i) this.anim = requestAnimationFrame(step);
        };
        this.anim = requestAnimationFrame(step);
      }
      render(i, local) {
        const ease = (x) => 1 - Math.pow(1 - Math.min(Math.max(x, 0), 1), 3);
        this.style.setProperty('--p' + (i + 1), ease(i === 2 ? local * 1.25 : local * 1.6).toFixed(3));
        for (let k = 0; k < this.scenes.length; k++) if (k < i) this.style.setProperty('--p' + (k + 1), 1);
        if (this.counter) {
          const to = parseInt(this.counter.dataset.count, 10) || 12;
          if (i === 1) this.counter.textContent = Math.round(to * ease(local * 1.4));
          else if (i > 1) this.counter.textContent = to;
        }
        if (this.arch) {
          const t = i > 2 ? 1 : i < 2 ? 0 : this.reduce ? 1 : Math.min(1, local * 1.15);
          if (t !== this.arch.t) {
            this.arch.t = t;
            const [p, squeeze] = this.chew(t);
            this.drawArch(p, squeeze);
          }
        }
        this.toggleAttribute('data-late4', i === 3 && local > 0.62);
      }
      tick() {
        this.raf = null;
        if (this.swipe) return;
        const r = this.getBoundingClientRect();
        const total = Math.max(1, this.offsetHeight - window.innerHeight);
        const t = Math.min(Math.max(-r.top / total, 0), 0.9999) * this.scenes.length;
        const i = Math.floor(t), local = this.reduce ? 1 : t - i;
        if (i !== this.current) this.setCurrent(i, false);
        this.render(i, local);
      }
    });
  }
